import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap, takeUntil } from 'rxjs/operators';
import { PollingService } from '../services/polling.service';
import { CancelService } from '../services/cancel.service';

/**
 * PollingHeaderInterceptor - Automatically detects polling headers and initiates polling
 * 
 * This interceptor listens to all HTTP responses and checks for specific headers:
 * - X-DeferredRequest-Id: The unique tracing identifier
 * - X-DeferredRequest-Domain: The domain of the request
 * - X-DeferredRequest-PollUrl: (Optional) Custom polling URL
 * 
 * When the required headers are present, it automatically triggers the polling service.
 * Ensures single trigger per response to prevent duplicate polling.
 */
@Injectable()
export class PollingHeaderInterceptor implements HttpInterceptor {
  // Track processed responses to prevent duplicate polling triggers
  private processedResponses = new Set<string>();

  constructor(
    private pollingService: PollingService,
    private cancelService: CancelService
  ) {
    // Clear processed responses on navigation to prevent memory leaks
    this.cancelService.cancel$.subscribe(() => {
      this.processedResponses.clear();
    });
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      takeUntil(this.cancelService.cancel$),
      tap({
        next: (event) => {
          // Only process HttpResponse events (not progress events)
          if (event instanceof HttpResponse) {
            this.checkForPollingHeaders(request, event);
          }
        }
      })
    );
  }

  /**
   * Checks response headers and triggers polling if required headers are present
   */
  private checkForPollingHeaders(request: HttpRequest<unknown>, response: HttpResponse<any>): void {
    if (request.headers.has('X-Skip-Polling')) {
      return;
    }

    const allHeaderKeys: string[] = [];
    response.headers.keys().forEach(key => {
      allHeaderKeys.push(key);
    });
    
    // Extract headers with new naming convention
    const traceId = response.headers.get('X-Deferredrequest-Id');
    const domain = response.headers.get('X-Deferredrequest-Domain');
    const pollUrl = response.headers.get('X-Deferredrequest-Pollurl');

    // Only proceed if both required headers exist
    if (!traceId || !domain) {
      return;
    }

    // Create unique key for this response
    const responseKey = `${traceId}:${domain}`;

    // Prevent duplicate polling for the same response
    if (this.processedResponses.has(responseKey)) {
      return;
    }

    // Mark as processed
    this.processedResponses.add(responseKey);

    // Trigger polling with optional custom poll URL
    this.pollingService.startPolling(traceId, domain, pollUrl || undefined);

    // Clean up old entries to prevent memory leak
    // Keep only last 100 processed responses
    if (this.processedResponses.size > 100) {
      const iterator = this.processedResponses.values();
      const firstValue = iterator.next().value;
      if (firstValue) {
        this.processedResponses.delete(firstValue);
      }
    }
  }
}
